import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import {
  usePromoProtectionPolicy,
  useStackingPolicy,
  useUpdatePromoProtectionPolicy,
  useUpdateStackingPolicy,
} from '../../hooks';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import type { CalculationStrategy } from '../../model/types';

export interface PromoSettingsPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

const ENGINE_MODES: { value: string; labelKey: string; available: boolean }[] = [
  { value: 'INTERNAL', labelKey: 'settings.engine.INTERNAL', available: true },
  { value: 'EXTERNAL_SYNC', labelKey: 'settings.engine.EXTERNAL_SYNC', available: false },
  { value: 'EXTERNAL_DELEGATED', labelKey: 'settings.engine.EXTERNAL_DELEGATED', available: false },
];

/** "Settings" — engine mode (static) + stacking policy + protection policy (plan-011 §8.9). */
export function PromoSettingsPanel({ onResult }: PromoSettingsPanelProps) {
  const { t } = useTranslation('promotions');
  const { canManage } = usePromoPermissions();

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('settings.engineCardTitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {ENGINE_MODES.map((mode) => (
              <Box
                key={mode.value}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: mode.value === 'INTERNAL' ? 'primary.main' : 'divider',
                  opacity: mode.available ? 1 : 0.7,
                  position: 'relative',
                }}
              >
                {mode.value === 'INTERNAL' ? (
                  <CheckRoundedIcon color="primary" fontSize="small" sx={{ position: 'absolute', top: 8, right: 8 }} />
                ) : (
                  <Chip size="small" label={t('settings.engine.comingSoon')} sx={{ position: 'absolute', top: 8, right: 8 }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, pr: 6 }}>
                  {t(mode.labelKey)}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('settings.engine.hint')}
          </Typography>
        </CardContent>
      </Card>

      <StackingPolicyCard canManage={canManage} onResult={onResult} />
      <ProtectionPolicyCard canManage={canManage} onResult={onResult} />
    </Stack>
  );
}

function StackingPolicyCard({
  canManage,
  onResult,
}: {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}) {
  const { t } = useTranslation('promotions');
  const query = useStackingPolicy();
  const update = useUpdateStackingPolicy();

  const [strategy, setStrategy] = useState<CalculationStrategy>('SEQUENTIAL');
  const [maxTotalDiscountPct, setMaxTotalDiscountPct] = useState('');
  const [combinesWithProduct, setCombinesWithProduct] = useState(true);
  const [combinesWithOrder, setCombinesWithOrder] = useState(true);
  const [combinesWithShipping, setCombinesWithShipping] = useState(true);
  const [dirty, setDirty] = useState(false);

  const resetFromQuery = () => {
    if (!query.data) return;
    setStrategy(query.data.calculationStrategy ?? 'SEQUENTIAL');
    setMaxTotalDiscountPct(query.data.maxTotalDiscountPct != null ? String(query.data.maxTotalDiscountPct) : '');
    const combo = (query.data.defaultCombinability ?? {}) as Record<string, boolean>;
    setCombinesWithProduct(combo.product ?? true);
    setCombinesWithOrder(combo.order ?? true);
    setCombinesWithShipping(combo.shipping ?? true);
    setDirty(false);
  };

  useEffect(resetFromQuery, [query.data]);

  if (query.isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <LoadingState label={t('settings.loading')} fullHeight={false} />
        </CardContent>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card variant="outlined">
        <CardContent>
          <ErrorState
            title={t('settings.loadError.title')}
            message={query.error?.message ?? t('common:error.unknown')}
            onRetry={() => void query.refetch()}
          />
        </CardContent>
      </Card>
    );
  }

  const strategyExample = { sequential: 72, parallel: 70, best: 90 };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('settings.stackingCardTitle')}
        </Typography>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('settings.applicationOrderLabel')}
            </Typography>
            <Stack direction="row" spacing={1}>
              {(query.data?.applicationOrder ?? ['PRODUCT', 'CART', 'SHIPPING']).map((step, index) => (
                <Chip key={step} size="small" label={`${index + 1}. ${step}`} />
              ))}
            </Stack>
          </Box>

          <TextField
            select
            label={t('settings.calculationStrategyLabel')}
            disabled={!canManage}
            value={strategy}
            onChange={(e) => {
              setStrategy(e.target.value as CalculationStrategy);
              setDirty(true);
            }}
            helperText={t('settings.strategyExample', strategyExample)}
            sx={{ maxWidth: 320 }}
          >
            <MenuItem value="SEQUENTIAL">{t('settings.strategy.SEQUENTIAL')}</MenuItem>
            <MenuItem value="PARALLEL">{t('settings.strategy.PARALLEL')}</MenuItem>
            <MenuItem value="BEST_DEAL">{t('settings.strategy.BEST_DEAL')}</MenuItem>
          </TextField>

          <TextField
            label={t('settings.maxTotalDiscountLabel')}
            type="number"
            disabled={!canManage}
            value={maxTotalDiscountPct}
            onChange={(e) => {
              setMaxTotalDiscountPct(e.target.value);
              setDirty(true);
            }}
            helperText={t('settings.maxTotalDiscountHint')}
            sx={{ maxWidth: 240 }}
          />

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('settings.defaultCombinabilityLabel')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={combinesWithProduct}
                disabled={!canManage}
                onChange={(e) => {
                  setCombinesWithProduct(e.target.checked);
                  setDirty(true);
                }}
              />
            }
            label={t('builder.combinesWithProduct')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={combinesWithOrder}
                disabled={!canManage}
                onChange={(e) => {
                  setCombinesWithOrder(e.target.checked);
                  setDirty(true);
                }}
              />
            }
            label={t('builder.combinesWithOrder')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={combinesWithShipping}
                disabled={!canManage}
                onChange={(e) => {
                  setCombinesWithShipping(e.target.checked);
                  setDirty(true);
                }}
              />
            }
            label={t('builder.combinesWithShipping')}
          />
        </Stack>

        {canManage ? (
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button disabled={!dirty || update.isPending} onClick={resetFromQuery}>
              {t('common:action.reset')}
            </Button>
            <Button
              variant="contained"
              disabled={!dirty || update.isPending}
              startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() =>
                update.mutate(
                  {
                    applicationOrder: query.data?.applicationOrder ?? ['PRODUCT', 'CART', 'SHIPPING'],
                    calculationStrategy: strategy,
                    maxTotalDiscountPct: maxTotalDiscountPct.trim() ? Number(maxTotalDiscountPct) : undefined,
                    defaultCombinability: {
                      product: combinesWithProduct,
                      order: combinesWithOrder,
                      shipping: combinesWithShipping,
                    },
                  },
                  {
                    onSuccess: () => {
                      onResult('success', t('settings.saved'));
                      setDirty(false);
                    },
                    onError: (e) => onResult('error', e.message),
                  },
                )
              }
            >
              {t('common:action.save')}
            </Button>
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('builder.readOnlyAlert')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function ProtectionPolicyCard({
  canManage,
  onResult,
}: {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}) {
  const { t } = useTranslation('promotions');
  const query = usePromoProtectionPolicy();
  const update = useUpdatePromoProtectionPolicy();

  const [maxDiscountPctAuto, setMaxDiscountPctAuto] = useState('50');
  const [forbidHundredPercent, setForbidHundredPercent] = useState(true);
  const [maxBudgetlessActive, setMaxBudgetlessActive] = useState('');
  const [dirty, setDirty] = useState(false);

  const resetFromQuery = () => {
    if (!query.data) return;
    setMaxDiscountPctAuto(String(query.data.maxDiscountPctAuto ?? 50));
    setForbidHundredPercent(query.data.forbidHundredPercentWithoutApproval ?? true);
    setMaxBudgetlessActive(query.data.maxBudgetlessActive != null ? String(query.data.maxBudgetlessActive) : '');
    setDirty(false);
  };

  useEffect(resetFromQuery, [query.data]);

  if (query.isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <LoadingState label={t('settings.loading')} fullHeight={false} />
        </CardContent>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card variant="outlined">
        <CardContent>
          <ErrorState
            title={t('settings.loadError.title')}
            message={query.error?.message ?? t('common:error.unknown')}
            onRetry={() => void query.refetch()}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('settings.protectionCardTitle')}
        </Typography>
        <Stack spacing={2}>
          <TextField
            label={t('settings.maxDiscountAutoLabel')}
            type="number"
            disabled={!canManage}
            value={maxDiscountPctAuto}
            onChange={(e) => {
              setMaxDiscountPctAuto(e.target.value);
              setDirty(true);
            }}
            helperText={t('settings.maxDiscountAutoHint')}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
            sx={{ maxWidth: 240 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={forbidHundredPercent}
                disabled={!canManage}
                onChange={(e) => {
                  setForbidHundredPercent(e.target.checked);
                  setDirty(true);
                }}
              />
            }
            label={t('settings.forbidHundredPercentLabel')}
          />
          <TextField
            label={t('settings.maxBudgetlessActiveLabel')}
            type="number"
            disabled={!canManage}
            value={maxBudgetlessActive}
            onChange={(e) => {
              setMaxBudgetlessActive(e.target.value);
              setDirty(true);
            }}
            helperText={t('common:field.optional')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ maxWidth: 240 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('settings.protectionPolicyFootnote')}
          </Typography>
        </Stack>

        {canManage ? (
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button disabled={!dirty || update.isPending} onClick={resetFromQuery}>
              {t('common:action.reset')}
            </Button>
            <Button
              variant="contained"
              disabled={!dirty || update.isPending}
              startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() =>
                update.mutate(
                  {
                    maxDiscountPctAuto: Number(maxDiscountPctAuto) || 0,
                    forbidHundredPercentWithoutApproval: forbidHundredPercent,
                    maxBudgetlessActive: maxBudgetlessActive.trim() ? Number(maxBudgetlessActive) : undefined,
                  },
                  {
                    onSuccess: () => {
                      onResult('success', t('settings.saved'));
                      setDirty(false);
                    },
                    onError: (e) => onResult('error', e.message),
                  },
                )
              }
            >
              {t('common:action.save')}
            </Button>
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('builder.readOnlyAlert')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
